import { createContext, useContext, useEffect, useMemo, useState } from "react";

const EN = {
    // Navbar + CTAs
    nav_homeowners: "Homeowners",
    nav_renters: "Renters",
    nav_moving: "Moving",
    nav_business: "Business",
    nav_texas: "Texas",
    cta_text_me: "Text me",
    cta_text_full: "Text me for your options",
    cta_call_me: "Call",
    cta_check_options: "Check my options",

    // Hero
    hero_chip: "Independent · Texas-based · I'm Jeffrey",
    hero_h1_a: "Why overpay for",
    hero_h1_b: "the services you already use?",
    hero_h1_c: "Keep that money in YOUR pocket.",
    hero_sub: "I'm Jeffrey — an independent representative. I help families and small businesses shop smarter on electricity, internet, phone, and more. Same reliable service you'd pick anyway, often at a better rate. Real human help, no robo-calls.",
    zip_placeholder: "Enter your ZIP code",

    // Trust band
    trust_know: "Know your options",
    trust_keep: "Keep more money",
    trust_pressure: "No pressure",

    // How it works
    how_chip: "How it works",
    how_title: "Four steps. Less than a minute to start.",

    // Essential services
    es_chip: "What I help with",
    es_title_a: "Essential services.",
    es_title_b: "Better value, no hassle.",
    es_sub: "I partner with national brands to offer families and small businesses more choice on the services you're already paying for every month — delivered by someone you can actually reach.",

    // Why overpay
    wo_chip: "Why overpay?",
    wo_title_a: "Same service.",
    wo_title_b: "Smarter rate.",
    wo_sub: "Your lights, phone, and internet don't change when you shop smart. The bill does. In a few minutes we'll see what options exist in your area — and whether any beat what you have now.",

    // Customer cards
    cc_chip: "Pick your situation",
    cc_title: "Who are you shopping for?",

    // Jeffrey intro
    ji_chip: "Hi, I'm Jeffrey",
    ji_title_a: "Real human help from",
    ji_title_b: "a Texan who cares.",

    // Meals / Impact counter
    meals_label: "Meals donated",
    meals_body: "Through my customers, we contribute meals to families via Project Feeding Kids.",
    helped_label: "Customers helped",
    helped_body: "Real households and small businesses whose bills we reviewed and improved.",

    // Opportunity teaser
    opp_teaser_chip: "For the curious",
    opp_teaser_title: "Interested in doing what I do?",
    opp_teaser_sub: "Separate flow — no sales pitch. Just a 15-min honest conversation if you're exploring a side income.",

    // About Jeffrey (ACN rep)
    about_chip: "What I do",
    about_title_a: "National brands,",
    about_title_b: "personal touch.",
    about_body: "I'm an independent representative who partners with national-brand providers so you can shop multiple essentials in one place — without the mega-corporation runaround. If I find a better fit for your household, I say so. If what you have is already great, I say that too.",
    about_point_1: "Multiple providers reviewed side-by-side so you see real choices.",
    about_point_2: "I only recommend what fits your situation — no bundling tricks.",
    about_point_3: "Every signed-up customer helps feed kids through Project Feeding Kids.",
    about_promise_chip: "My promise",
    about_promise_title: "No fine-print surprises.",
    about_promise_1: "I'll always tell you the full contract length",
    about_promise_2: "I'll always show you the base fees up front",
    about_promise_3: "I'll never auto-enroll you in anything",
    about_promise_4: "I'll help you cancel if a plan isn't working",

    // Giving back
    gb_chip: "Giving back",
    gb_title_a: "Every new customer helps",
    gb_title_b: "feed a child in need.",
    gb_body: "I'm proud to work with partners that donate meals to families fighting food insecurity through Project Feeding Kids. When you sign up for a plan through me, you're not just saving money — you're helping a kid eat.",
    gb_badge: "Project Feeding Kids",
    gb_badge_sub: "Meals donated with every sign-up",

    // FAQ
    faq_chip: "Questions, answered",
    faq_title: "What you probably want to ask.",
    faq_sub: "If anything's unclear, text me — I'll answer in plain English, not scripts.",
    faq_q1: "Is this a scam or an MLM?",
    faq_a1: "No. I'm an independent representative who partners with legitimate national-brand providers (think electricity, internet, phone companies you've heard of). I earn a referral when you choose a plan through me — same way an insurance broker works. You pay the same price either way.",
    faq_q2: "Will my service actually change?",
    faq_a2: "Your lights still come from the same utility wires. Your phone keeps the same tower coverage. The company billing you may change, but the service delivered to your door does not. That's why switching smart saves money without lowering quality.",
    faq_q3: "Is there any fee to talk with you?",
    faq_a3: "Zero. I never charge you to review options or help you sign up. If you pick a plan, that provider pays me a referral — same price for you as going direct.",
    faq_q4: "What if the new plan doesn't work out?",
    faq_a4: "Most plans have an early-exit term I'll walk you through before you sign anything. If something feels off, text me and I'll help you fix it. I don't disappear after sign-up.",
    faq_q5: "What services can you help with?",
    faq_a5: "Electricity (my specialty in Texas), internet, mobile phone, TV, home security, natural gas where available, and small-business payment processing. If you have a bill, let me look at it.",
    faq_q6: "How soon do you respond?",
    faq_a6: "I aim to text or call back within 15 minutes during the day. If it's after hours, you'll hear from me first thing in the morning. For urgent moves, call or text — don't wait on email.",

    // Lead form
    form_step_you: "You",
    form_step_location: "Location",
    form_step_situation: "Situation",
    form_step_usage: "Usage",
    form_step_contact: "Contact",
    form_step_review: "Review",
    form_back: "Back",
    form_next: "Continue",
    form_submit: "Get help comparing options",
    form_submitting: "Submitting...",

    // Thank you
    ty_title: "You're in. We got your request.",
    ty_sub: "A real person will reach out shortly through the contact method you chose. You can also skip the wait - reach out directly below.",
    ty_next: "What happens next",

    // Disclosure
    disclosure_strong: "Independent representative.",
    disclosure_body: "Plans, rates, fees, terms, eligibility, and availability vary by location and usage. Information is provided for comparison purposes. Availability and pricing should be verified before enrollment. We are not the utility company.",

    // Language toggle
    lang_label: "Language",
};

const ES = {
    nav_homeowners: "Propietarios",
    nav_renters: "Inquilinos",
    nav_moving: "Mudanza",
    nav_business: "Negocios",
    nav_texas: "Texas",
    cta_text_me: "Textéame",
    cta_text_full: "Mándame un texto con mis opciones",
    cta_call_me: "Llámame",
    cta_check_options: "Ver mis opciones",

    hero_chip: "Independiente · Texas · Soy Jeffrey",
    hero_h1_a: "¿Por qué pagar de más por",
    hero_h1_b: "los servicios que ya usas?",
    hero_h1_c: "Guarda ese dinero en TU bolsillo.",
    hero_sub: "Soy Jeffrey, un representante independiente. Ayudo a familias y pequeños negocios a comprar con inteligencia la electricidad, el internet, el teléfono y más. El mismo servicio confiable, con mejores tarifas cuando existen. Ayuda humana real, sin llamadas automáticas.",
    zip_placeholder: "Ingresa tu código postal",

    trust_know: "Conoce tus opciones",
    trust_keep: "Guarda más dinero",
    trust_pressure: "Sin presión",

    how_chip: "Cómo funciona",
    how_title: "Cuatro pasos. Menos de un minuto para empezar.",

    es_chip: "En lo que ayudo",
    es_title_a: "Servicios esenciales.",
    es_title_b: "Más valor, sin complicaciones.",
    es_sub: "Trabajo con marcas nacionales para ofrecer a familias y pequeños negocios más opciones en los servicios que ya pagas cada mes — con alguien al que puedes llamar.",

    wo_chip: "¿Por qué pagar de más?",
    wo_title_a: "El mismo servicio.",
    wo_title_b: "Tarifas más inteligentes.",
    wo_sub: "Tus luces, teléfono e internet no cambian cuando compras con inteligencia. La cuenta sí. En pocos minutos vemos qué opciones existen en tu zona — y si alguna supera lo que tienes hoy.",

    cc_chip: "Elige tu situación",
    cc_title: "¿Para quién estás comparando?",

    ji_chip: "Hola, soy Jeffrey",
    ji_title_a: "Ayuda humana real de",
    ji_title_b: "un tejano que se preocupa.",

    meals_label: "Comidas donadas",
    meals_body: "A través de mis clientes, contribuimos con comidas a familias mediante Project Feeding Kids.",
    helped_label: "Clientes ayudados",
    helped_body: "Hogares y pequeños negocios reales cuyas facturas revisamos y mejoramos.",

    opp_teaser_chip: "Para los curiosos",
    opp_teaser_title: "¿Te interesa hacer lo que yo hago?",
    opp_teaser_sub: "Flujo separado — sin discurso de ventas. Solo una conversación honesta de 15 min si estás explorando un ingreso extra.",

    about_chip: "Qué hago",
    about_title_a: "Marcas nacionales,",
    about_title_b: "trato personal.",
    about_body: "Soy un representante independiente que trabaja con proveedores de marcas nacionales para que puedas revisar varios esenciales en un solo lugar — sin tener que lidiar con una megaempresa. Si encuentro algo mejor para tu hogar, te lo digo. Si lo que tienes ya está bien, también te lo digo.",
    about_point_1: "Comparo varios proveedores lado a lado para que veas opciones reales.",
    about_point_2: "Solo recomiendo lo que te sirve — nada de trucos para empaquetar.",
    about_point_3: "Cada cliente ayuda a alimentar niños a través de Project Feeding Kids.",
    about_promise_chip: "Mi promesa",
    about_promise_title: "Sin sorpresas en letra chica.",
    about_promise_1: "Siempre te digo la duración completa del contrato",
    about_promise_2: "Siempre te muestro los cargos base por adelantado",
    about_promise_3: "Nunca te inscribo automáticamente en nada",
    about_promise_4: "Te ayudo a cancelar si un plan no funciona",

    gb_chip: "Devolviendo",
    gb_title_a: "Cada cliente nuevo ayuda a",
    gb_title_b: "alimentar a un niño que lo necesita.",
    gb_body: "Me enorgullece trabajar con socios que donan comidas a familias que combaten el hambre a través de Project Feeding Kids. Cuando te registras conmigo, no solo ahorras dinero — ayudas a que un niño coma.",
    gb_badge: "Project Feeding Kids",
    gb_badge_sub: "Comidas donadas con cada registro",

    faq_chip: "Respuestas",
    faq_title: "Lo que probablemente quieres preguntar.",
    faq_sub: "Si algo no queda claro, mándame un texto — te respondo en español sencillo, sin libretos.",
    faq_q1: "¿Esto es una estafa o pirámide?",
    faq_a1: "No. Soy un representante independiente que trabaja con proveedores de marcas nacionales (como las compañías de luz, internet y teléfono que ya conoces). Gano una referencia cuando eliges un plan a través de mí — igual que un corredor de seguros. Tú pagas el mismo precio.",
    faq_q2: "¿Realmente cambiará mi servicio?",
    faq_a2: "La luz sigue viniendo por los mismos cables de la empresa de servicios públicos. Tu teléfono mantiene la misma cobertura. La compañía que te factura puede cambiar, pero el servicio que llega a tu puerta no. Por eso cambiar con inteligencia ahorra dinero sin bajar calidad.",
    faq_q3: "¿Hay algún costo por hablar contigo?",
    faq_a3: "Cero. Nunca te cobro por revisar opciones o ayudarte a registrarte. Si eliges un plan, ese proveedor me paga una referencia — mismo precio para ti que si fueras directo.",
    faq_q4: "¿Y si el nuevo plan no funciona?",
    faq_a4: "La mayoría de los planes tienen un término de salida anticipada que te explico antes de firmar nada. Si algo se siente mal, mándame un texto y te ayudo a arreglarlo. No desaparezco después del registro.",
    faq_q5: "¿Con qué servicios puedes ayudarme?",
    faq_a5: "Electricidad (mi especialidad en Texas), internet, teléfono celular, TV, seguridad del hogar, gas natural donde esté disponible, y procesamiento de pagos para pequeños negocios. Si tienes una factura, déjame revisarla.",
    faq_q6: "¿Qué tan rápido respondes?",
    faq_a6: "Trato de responder por texto o llamada en menos de 15 minutos durante el día. Si es fuera de horario, me escucharás a primera hora. Para mudanzas urgentes, llama o manda texto — no esperes al email.",

    form_step_you: "Tú",
    form_step_location: "Ubicación",
    form_step_situation: "Situación",
    form_step_usage: "Consumo",
    form_step_contact: "Contacto",
    form_step_review: "Revisar",
    form_back: "Atrás",
    form_next: "Continuar",
    form_submit: "Obtén ayuda para comparar opciones",
    form_submitting: "Enviando...",

    ty_title: "Listo. Recibimos tu solicitud.",
    ty_sub: "Una persona real te contactará pronto por el método que elegiste. También puedes contactarnos directamente abajo si no quieres esperar.",
    ty_next: "Qué sigue",

    disclosure_strong: "Representante independiente.",
    disclosure_body: "Los planes, tarifas, cargos, términos, elegibilidad y disponibilidad varían según la ubicación y el consumo. La información se proporciona solo con fines de comparación. Verifica la disponibilidad y el precio antes de inscribirte. No somos la compañía eléctrica.",

    lang_label: "Idioma",
};

const DICTS = { en: EN, es: ES };

const LangContext = createContext({ lang: "en", setLang: () => {}, t: (k) => EN[k] || k });

export function LangProvider({ children }) {
    const [lang, setLang] = useState(() => {
        if (typeof window === "undefined") return "en";
        return window.localStorage.getItem("ps_lang") || "en";
    });

    useEffect(() => {
        if (typeof window !== "undefined") {
            window.localStorage.setItem("ps_lang", lang);
            document.documentElement.setAttribute("lang", lang);
        }
    }, [lang]);

    const value = useMemo(() => {
        const dict = DICTS[lang] || EN;
        return {
            lang,
            setLang,
            t: (key) => dict[key] || EN[key] || key,
        };
    }, [lang]);

    return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useT() {
    return useContext(LangContext);
}
